import { Collapse, Row, Col, Image, Typography, Carousel } from "antd";
import { FieldTimeOutlined, CaretRightOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import "./App.css";

const { Panel } = Collapse;
const { Title, Text } = Typography;

const getOptimizedImage = (name, alt) => ({
  alt,
  avif: `/images/${name}-480.avif 480w, /images/${name}-800.avif 800w, /images/${name}-1200.avif 1200w`,
  webp: `/images/${name}-480.webp 480w, /images/${name}-800.webp 800w, /images/${name}-1200.webp 1200w`,
  fallback: `/images/${name}-800.webp`,
});

const renderResponsiveImage = (image, width, height, eager = false) => {
  if (typeof image === "string") {
    return (
      <Image
        src={image}
        width={width}
        height={height}
        style={{ objectFit: "cover" }}
        preview={false}
        loading={eager ? "eager" : "lazy"}
      />
    );
  }

  return (
    <picture>
      <source srcSet={image.avif} sizes="(max-width: 600px) 480px, (max-width: 992px) 800px, 1200px" type="image/avif" />
      <source srcSet={image.webp} sizes="(max-width: 600px) 480px, (max-width: 992px) 800px, 1200px" type="image/webp" />
      <img
        src={image.fallback}
        alt={image.alt}
        width={width}
        height={height}
        style={{ width, height, objectFit: "cover" }}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={eager ? "high" : "auto"}
      />
    </picture>
  );
};

const ImageSlider = ({
  images = [],
  width = 300,
  height = 400,
  enablePreview = false,
  fallbackSrc,
}) => {
  const list = useMemo(() => (images?.length ? images : []), [images]);
  const many = list.length > 1;

  if (!list.length) {
    return fallbackSrc ? (
      <Image
        src={fallbackSrc}
        width={width}
        height={height}
        style={{ objectFit: "cover" }}
        preview={false}
      />
    ) : null;
  }

  if (!many) {
    const src = list[0];
    return renderResponsiveImage(src, width, height, true);
  }

  return (
    <Image.PreviewGroup preview={enablePreview ? {} : false}>
      <Carousel dots arrows infinite draggable speed={250}>
        {list.map((src, i) => (
          <div key={`${typeof src === "string" ? src : src.fallback}-${i}`} style={{ padding: 4 }}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              {renderResponsiveImage(src, width, height, i === 0)}
            </div>
          </div>
        ))}
      </Carousel>
    </Image.PreviewGroup>
  );
};

const BOOKING_FAQ = [
  {
    id: "portrait",
    question: "Хөрөг зургийн төрлөөр (Биеийн дээд хэсгийн зураг авалттай)",
    answer:
      "Үүнд: <br/>- Төрсөн өдрийн багц (30/60мин) <br/>- Portrait багц (30/60мин) <br/>- Жирэмсний 60мин багц <br/>Та <a href='https://booking.picshot.online/portrait' target='_blank' rel='noreferrer'>энд дарж</a> цаг захиална уу.",
    images: [
      getOptimizedImage("IMG_0648", "Portrait sample"),
      getOptimizedImage("IMG_3609", "Portrait sample"),
      getOptimizedImage("IMG_4452", "Portrait sample"),
    ],
  },
  {
    id: "fullbody",
    question: "Бүтэн бие орсон төрлөөр",
    answer:
      "Үүнд: <br/>- Энгийн багц (30/60мин) <br/>- Хүүхдийн төрсөн өдөр (60мин) <br/>- Жирэмсний 60мин багц <br/>Та <a href='https://booking.picshot.online/regular' target='_blank' rel='noreferrer'>энд дарж</a> цаг захиална уу.",
    images: [
      getOptimizedImage("IMG_9363", "Regular sample"),
      getOptimizedImage("IMG_4242", "Regular sample"),
      getOptimizedImage("IMG_7829", "Regular sample"),
    ],
  },
];

const WARN_FAQ = [
  {
    id: "w1",
    question: "1. Хүүхдийн зураг авалт ямар хугацаанд дуусах вэ?",
    answer: "30 минут орчим.",
  },
  {
    id: "w2",
    question: "2. Студийн байрлал хаана?",
    answer: "Улаанбаатар хотын төвд.",
  },
  {
    id: "w3",
    question: "3. Төлбөр яаж хийх вэ?",
    answer: "Карт, бэлэн, онлайн.",
  },
  {
    id: "w4",
    question: "4. Дижитал файлыг яаж авах вэ?",
    answer: "Имэйл эсвэл апп-аар.",
  },
  {
    id: "w5",
    question: "5. Цаг тохируулахад юу хэрэгтэй вэ?",
    answer: "Нэр, утас, хүссэн цаг.",
  },
  { id: "w6", question: "6. Үнэ?", answer: "Вэбсайт/утаснаас лавлана." },
  {
    id: "w7",
    question: "7. Үйлчилгээнд юу багтдаг вэ?",
    answer: "Зөвлөмж, хэрэгсэл, хувцас гэх мэт.",
  },
  {
    id: "w8",
    question: "8. Сессийн явцад би юу авах вэ?",
    answer: "Зураг, хөтөч болон бусад зүйлс.",
  },
  {
    id: "w9",
    question: "9. Нэмэлт үйлчилгээ?",
    answer: "Хүүхэд, гэр бүл гэх мэт.",
  },
  {
    id: "w10",
    question: "10. Төрлүүд?",
    answer: "Энгийн, портрет, хүүхдийн зураг авалт.",
  },
  {
    id: "w11",
    question: "11. Онлайн захиалга?",
    answer: "Тийм, хурдан хялбар.",
  },
];

const FAQSection = () => {
  const [visibleWarnCount, setVisibleWarnCount] = useState(5);
  const canLoadMoreWarn = visibleWarnCount < WARN_FAQ.length;

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "12px 16px" }}>
      <Row justify="center" gutter={[16, 24]}>
        <Col xs={24} sm={20} md={16} lg={12}>
          <Title level={1} style={{ textAlign: "center", marginBottom: 6 }}>
            <Text style={{ fontSize: "24px", color: "revert" }}>
              <FieldTimeOutlined /> Цаг авах
            </Text>
          </Title>

          <Collapse
            accordion
            style={{ background: "#83d6a6", color: "black" }}
            defaultActiveKey={["portrait"]}
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
          >
            {BOOKING_FAQ.map((item) => (
              <Panel header={item.question} key={item.id}>
                <ImageSlider
                  images={item.images}
                  width={300}
                  height={400}
                  enablePreview={false}
                />
                <div
                  style={{ lineHeight: 1.7, marginTop: 12 }}
                  dangerouslySetInnerHTML={{ __html: item.answer }}
                />
              </Panel>
            ))}
          </Collapse>
        </Col>
      </Row>

      {false && (
        <Row justify="center" gutter={[16, 24]} style={{ marginTop: 12 }}>
          <Col xs={24} sm={20} md={16} lg={12}>
            <Collapse
              accordion
              style={{ background: "#d68583", color: "black" }}
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
            >
              {WARN_FAQ.slice(0, visibleWarnCount).map((item) => (
                <Panel header={item.question} key={item.id}>
                  <div style={{ lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: item.answer }} />
                </Panel>
              ))}
            </Collapse>

            <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
              <button disabled={!canLoadMoreWarn} onClick={() => setVisibleWarnCount((n) => n + 5)}>
                Цааш үзэх
              </button>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default FAQSection;
